import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  AlertTriangle,
  BadgeCheck,
  Check,
  ChevronRight,
  Loader2,
  MapPin,
  PackageSearch,
  RotateCcw,
  ShieldCheck,
  ShoppingCart,
  Star,
  Store,
  Truck,
  Zap,
} from 'lucide-react';
import { ShopChrome, Crumbs } from '@/components/shop/ShopChrome';
import ProductGallery from '@/components/shop/ProductGallery';
import VariantPicker from '@/components/shop/VariantPicker';
import ProductReviews from '@/components/shop/ProductReviews';
import { ProductRail, WishlistHeart } from '@/components/shop/ProductCard';
import { PriceTag, QtyStepper, RatingPill, Sheet } from '@/components/shop/ShopUI';
import { EmptyState, Shimmer } from '@/components/ui';
import { addToCart, getCart, getProduct } from '@/api/shop';
import { useAuth } from '@/context/AuthContext';
import { syncCartCount, useLoginThen, useShopState } from '@/lib/shopStore';
import { useDocumentMeta } from '@/lib/useDocumentMeta';
import { rememberViewed } from '@/lib/shopHome';
import { friendlyError } from '@/lib/apiError';
import { deriveOptionStates, rupees } from '@/lib/shop';

const ProductSkeleton = () => (
  <div className="lk-container py-6">
    <Shimmer className="h-4 w-64" />
    <div className="mt-4 grid gap-8 md:grid-cols-2">
      <Shimmer className="aspect-[4/5] w-full rounded-2xl" />
      <div className="space-y-4">
        <Shimmer className="h-4 w-24" />
        <Shimmer className="h-8 w-full" />
        <Shimmer className="h-5 w-32" />
        <Shimmer className="h-10 w-48" />
        <Shimmer className="h-24 w-full" />
        <Shimmer className="h-12 w-full" />
      </div>
    </div>
  </div>
);

const SizeChart = ({ chart }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[320px] text-sm">
      <thead>
        <tr className="bg-surface-muted">
          {chart.columns.map((c) => (
            <th key={c} className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {chart.rows.map((row) => (
          <tr key={row.join('|')} className="border-b border-ink-200/70 last:border-0">
            {row.map((cell, i) => (
              <td key={i} className={`tabular px-3 py-2.5 ${i === 0 ? 'font-semibold text-ink-900' : 'text-ink-700'}`}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
    <p className="mt-3 text-xs text-ink-500">Body measurements. If you are between sizes, pick the larger one.</p>
  </div>
);

const ProductPage = () => {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const loginThen = useLoginThen();
  const { freeDeliveryAbove } = useShopState();

  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(null); // listingId being added
  const [chartOpen, setChartOpen] = useState(false);

  // Fetch once per product. Variant switches after that are local (every
  // variant's price, images and offers come in the one response), so the
  // price changes the instant a chip is tapped.
  useEffect(() => {
    let live = true;
    setDetail(null);
    setError(null);
    const want = Number(new URLSearchParams(window.location.search).get('variant')) || undefined;
    getProduct(slug, want)
      .then((d) => {
        if (!live) return;
        setDetail(d);
        setSelectedId(d.selectedVariantId);
      })
      .catch((err) => live && setError({ status: err?.response?.status, message: friendlyError(err, 'This product could not be loaded') }));
    return () => {
      live = false;
    };
  }, [slug]);

  // Back/forward or a pasted link changing ?variant= on the same product.
  const urlVariant = Number(params.get('variant')) || null;
  useEffect(() => {
    if (!detail || !urlVariant || urlVariant === selectedId) return;
    if (detail.variants.some((v) => v.id === urlVariant)) setSelectedId(urlVariant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlVariant, detail]);

  const variants = useMemo(() => detail?.variants || [], [detail]);
  const selected = useMemo(() => variants.find((v) => v.id === selectedId) || variants[0], [variants, selectedId]);
  const theme = useMemo(() => detail?.variationTheme || [], [detail]);

  const optionStates = useMemo(() => {
    if (!detail || !selected) return {};
    if (detail.selectedVariantId === selected.id && detail.optionStates) return detail.optionStates;
    return deriveOptionStates(theme, variants, selected);
  }, [detail, selected, theme, variants]);

  // Show each option's price on its chip only when variants are priced differently.
  const pricesDiffer = useMemo(() => new Set(variants.map((v) => v.featuredOffer?.price ?? v.price)).size > 1, [variants]);

  const offer = selected?.featuredOffer || null;
  const maxQty = offer ? Math.max(1, Math.min(offer.maxPerOrder || 10, offer.available || 1)) : 1;

  useEffect(() => {
    setQty((q) => Math.min(q, maxQty));
  }, [maxQty]);

  const selectVariant = useCallback(
    (id, jumped) => {
      setSelectedId(id);
      const next = new URLSearchParams(params);
      next.set('variant', String(id));
      setParams(next, { replace: true });
      if (jumped) {
        const v = variants.find((x) => x.id === id);
        if (v) toast(`Switched to ${v.label}`, { id: 'variant-jump', icon: '↔' });
      }
    },
    [params, setParams, variants]
  );

  // The free-delivery threshold is configured on the backend and comes with
  // any cart view; a signed-in shopper's cart is fetched once to learn it.
  useEffect(() => {
    if (user && freeDeliveryAbove == null) getCart().then(syncCartCount).catch(() => {});
  }, [user, freeDeliveryAbove]);

  // Feed the shop home's "Recently viewed" row (localStorage, newest first).
  useEffect(() => {
    if (!detail?.product || !selected) return;
    const p = detail.product;
    const o = selected.featuredOffer;
    rememberViewed({
      slug,
      title: p.title,
      image: selected.media?.[0] || p.media?.[0] || null,
      price: o?.price ?? selected.price,
      mrp: o?.mrp ?? selected.mrp,
      discountPercent: o?.discountPercent,
      brand: p.brand?.name,
    });
    // Once per product visit, not per variant tap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail]);

  const add = async (listingId, n = qty, then) => {
    if (!user) {
      // Added for them once they have logged in (they stay on this page).
      loginThen({ type: 'cart', listingId, qty: n });
      return;
    }
    setBusy(listingId);
    try {
      const view = await addToCart(listingId, n);
      syncCartCount(view);
      if (then) then();
      else
        toast.success(
          (t) => (
            <span className="flex items-center gap-3">
              Added to cart
              <button
                type="button"
                onClick={() => {
                  toast.dismiss(t.id);
                  navigate('/shop/cart');
                }}
                className="rounded-lg bg-brand-primary px-2.5 py-1 text-xs font-semibold text-white"
              >
                View cart
              </button>
            </span>
          ),
          { id: 'added' }
        );
    } catch (err) {
      toast.error(friendlyError(err, 'Could not add to cart'));
    } finally {
      setBusy(null);
    }
  };

  const buyNow = () => {
    if (!offer) return;
    const to = `/shop/checkout?buy=${offer.listingId}:${qty}&from=${encodeURIComponent(slug)}`;
    if (!user) {
      loginThen({ type: 'go', to });
      return;
    }
    navigate(to);
  };

  // Title, description, OG and Product JSON-LD once the product is in.
  const metaProduct = detail?.product;
  const metaOffer = selected?.featuredOffer;
  const metaImage = (selected?.media?.[0] || metaProduct?.media?.[0]) ?? null;
  useDocumentMeta(
    metaProduct
      ? {
          title: [metaProduct.brand?.name, metaProduct.title].filter(Boolean).join(' '),
          description:
            metaProduct.description ||
            `${metaProduct.title}${metaOffer ? ` for ${rupees(metaOffer.price)}` : ''} — buy online from local stores on LK Shop.`,
          image: metaImage,
          jsonLd: {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: metaProduct.title,
            ...(metaImage ? { image: [metaImage] } : {}),
            ...(metaProduct.description ? { description: metaProduct.description.slice(0, 5000) } : {}),
            ...(selected?.sku ? { sku: selected.sku } : {}),
            ...(metaProduct.brand?.name ? { brand: { '@type': 'Brand', name: metaProduct.brand.name } } : {}),
            ...(metaOffer
              ? {
                  offers: {
                    '@type': 'Offer',
                    url: typeof window !== 'undefined' ? `${window.location.origin}/shop/p/${slug}?variant=${selected.id}` : undefined,
                    priceCurrency: 'INR',
                    price: Number(metaOffer.price).toFixed(2),
                    availability:
                      selected.inStock && metaOffer.available > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
                    itemCondition: 'https://schema.org/NewCondition',
                    ...(metaOffer.seller?.name ? { seller: { '@type': 'Organization', name: metaOffer.seller.name } } : {}),
                  },
                }
              : {}),
            ...(metaProduct.rating?.count > 0
              ? {
                  aggregateRating: {
                    '@type': 'AggregateRating',
                    ratingValue: Number(metaProduct.rating.average).toFixed(1),
                    reviewCount: metaProduct.rating.count,
                  },
                }
              : {}),
          },
        }
      : null
  );

  if (error) {
    return (
      <ShopChrome>
        <div className="lk-container py-12">
          <EmptyState
            icon={PackageSearch}
            title={error.status === 404 ? 'Product not found' : 'Something went wrong'}
            message={error.status === 404 ? 'It may have been removed or is no longer sold.' : error.message}
            action={<Link to="/shop" className="lk-btn-primary">Back to the shop</Link>}
          />
        </div>
      </ShopChrome>
    );
  }

  if (!detail || !selected) {
    return (
      <ShopChrome>
        <ProductSkeleton />
      </ShopChrome>
    );
  }

  const product = detail.product;
  const images = selected.media?.length ? selected.media : product.media;
  const price = offer?.price ?? selected.price;
  const mrp = offer?.mrp ?? selected.mrp;
  const discount = offer?.discountPercent;
  const inStock = Boolean(selected.inStock && offer && offer.available > 0);
  const others = selected.otherOffers || [];
  const keySpecs = (detail.specs || []).filter((s) => s.highlight);

  const crumbs = [
    { name: 'Shop', to: '/shop' },
    ...(product.breadcrumbs || []).map((b) => ({ name: b.name, to: `/shop/c/${b.slug}` })),
    { name: product.title },
  ];

  const ctaButtons = (compact) => (
    <>
      <button
        type="button"
        onClick={() => add(offer.listingId)}
        disabled={!inStock || busy != null}
        className={`lk-btn flex-1 border-2 border-brand-primary bg-white text-brand-primary hover:bg-surface-muted ${compact ? 'px-3' : ''}`}
      >
        {busy === offer?.listingId ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
        Add to cart
      </button>
      <button type="button" onClick={buyNow} disabled={!inStock} className={`lk-btn-primary flex-1 ${compact ? 'px-3' : ''}`}>
        <Zap className="h-4 w-4" />
        Buy now
      </button>
    </>
  );

  return (
    <ShopChrome>
      <div className="lk-container py-4 sm:py-6">
        <Crumbs items={crumbs} />

        <div className="mt-4 grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-10">
          {/* Gallery */}
          <div className="md:sticky md:top-24 md:self-start">
            <ProductGallery images={images} title={product.title} resetKey={selected.id}>
              <WishlistHeart productId={product.id} variantId={selected.id} className="absolute right-3 top-3 h-11 w-11" />
            </ProductGallery>
          </div>

          {/* Buy box */}
          <div className="min-w-0">
            {product.brand && (
              <Link to={`/shop/search?brand=${product.brand.slug}`} className="lk-focus rounded text-sm font-semibold uppercase tracking-wide text-brand-secondary hover:underline">
                {product.brand.name}
              </Link>
            )}
            <h1 className="mt-1 text-xl font-semibold leading-snug text-ink-900 sm:text-2xl" style={{ fontFamily: 'inherit' }}>
              {product.title}
            </h1>
            {product.rating?.count > 0 && (
              <a href="#reviews" className="lk-focus mt-2 inline-flex rounded">
                <RatingPill value={product.rating.average} count={product.rating.count} />
                <span className="ml-1.5 text-xs text-ink-500">ratings</span>
              </a>
            )}

            {/* Price block */}
            <div className="mt-4 rounded-2xl bg-white p-4 ring-1 ring-ink-200/70">
              {discount > 0 && <p className="mb-1 text-xs font-bold uppercase tracking-wide text-success">Special price</p>}
              <PriceTag price={price} mrp={mrp} discount={discount} size="lg" />
              <p className="mt-1.5 text-xs text-ink-500">
                Inclusive of all taxes
                {selected.unitPrice && (
                  <span className="tabular ml-2 rounded bg-surface-muted px-1.5 py-0.5 font-medium text-ink-700">
                    {rupees(selected.unitPrice.amount)} / {selected.unitPrice.per}
                  </span>
                )}
                {selected.netQuantity && !selected.unitPrice && <span className="ml-2">Net qty {selected.netQuantity} {selected.netUnit}</span>}
              </p>
              {inStock && offer.available <= 5 && (
                <p className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-danger">
                  <AlertTriangle className="h-4 w-4" /> Only {offer.available} left
                </p>
              )}
              {!inStock && (
                <p className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-danger-tint px-2.5 py-1.5 text-sm font-semibold text-danger">
                  <AlertTriangle className="h-4 w-4" /> {offer ? `${selected.label || 'This item'} is out of stock` : 'Currently unavailable'}
                </p>
              )}
            </div>

            {/* Variants */}
            {theme.length > 0 && (
              <div className="mt-5">
                <VariantPicker
                  theme={theme}
                  variants={variants}
                  selected={selected}
                  optionStates={optionStates}
                  onSelect={selectVariant}
                  onSizeChart={detail.sizeChart ? () => setChartOpen(true) : null}
                  showPrices={pricesDiffer}
                />
              </div>
            )}

            {/* Delivery + seller */}
            {offer && (
              <div className="mt-5 divide-y divide-ink-200/70 rounded-2xl bg-white ring-1 ring-ink-200/70">
                <div className="flex gap-3 p-4">
                  <Truck className="mt-0.5 h-5 w-5 shrink-0 text-ink-700" />
                  <div className="text-sm">
                    <p className="font-semibold text-ink-900">
                      Delivery by <span className="text-success">{offer.delivery?.label}</span>
                    </p>
                    <p className="text-ink-500">
                      Ships in {offer.dispatchDays} day{offer.dispatchDays === 1 ? '' : 's'} · Free delivery on orders above {rupees(freeDeliveryAbove ?? 499)} from this store
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 p-4">
                  <Store className="mt-0.5 h-5 w-5 shrink-0 text-ink-700" />
                  <div className="min-w-0 flex-1 text-sm">
                    <p className="text-ink-500">
                      Sold by{' '}
                      <Link to={`/shop/store/${offer.seller.slug}`} className="lk-focus rounded font-semibold text-brand-secondary hover:underline">
                        {offer.seller.name}
                      </Link>
                      {offer.seller.rating > 0 && (
                        <span className="ml-2 inline-flex translate-y-0.5">
                          <RatingPill value={offer.seller.rating} />
                        </span>
                      )}
                    </p>
                    {others.length > 0 && (
                      <a href="#other-sellers" className="lk-focus mt-0.5 inline-flex items-center gap-0.5 rounded text-xs font-semibold text-ink-700 hover:underline">
                        {others.length} more seller{others.length > 1 ? 's' : ''} from {rupees(Math.min(...others.map((o) => o.price)))}
                        <ChevronRight className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex gap-3 p-4">
                  <RotateCcw className="mt-0.5 h-5 w-5 shrink-0 text-ink-700" />
                  <p className="text-sm text-ink-700">
                    {product.returnWindowDays > 0 ? `${product.returnWindowDays}-day return after delivery` : 'This item is not returnable'}
                  </p>
                </div>
              </div>
            )}

            {/* Qty + CTAs (desktop; phones get the sticky bar) */}
            {offer && (
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <span className="text-sm text-ink-500">Quantity</span>
                <QtyStepper value={qty} max={maxQty} onChange={setQty} disabled={!inStock} />
                {inStock && offer.maxPerOrder <= offer.available && <span className="text-xs text-ink-400">Max {offer.maxPerOrder} per order</span>}
              </div>
            )}
            {offer ? (
              <div className="mt-4 hidden gap-3 lg:flex">{ctaButtons(false)}</div>
            ) : (
              // No seller offers this variant right now: say so where the buttons would be.
              <div className="mt-4 flex items-start gap-3 rounded-2xl bg-surface-muted p-4 ring-1 ring-ink-200/70" role="status">
                <PackageSearch className="mt-0.5 h-5 w-5 shrink-0 text-ink-500" />
                <div className="text-sm">
                  <p className="font-semibold text-ink-900">Currently unavailable</p>
                  <p className="mt-0.5 text-ink-500">
                    No store is selling {theme.length ? selected.label : 'this item'} right now.
                    {variants.some((v) => v.id !== selected.id && v.featuredOffer) ? ' Try another option above.' : ' Save it to your wishlist and check back later.'}
                  </p>
                </div>
              </div>
            )}

            <ul className="mt-5 grid grid-cols-3 gap-2 text-center text-[11px] text-ink-500">
              <li className="flex flex-col items-center gap-1 rounded-xl bg-white p-2.5 ring-1 ring-ink-200/70">
                <ShieldCheck className="h-5 w-5 text-ink-700" /> Secure payment
              </li>
              <li className="flex flex-col items-center gap-1 rounded-xl bg-white p-2.5 ring-1 ring-ink-200/70">
                <BadgeCheck className="h-5 w-5 text-ink-700" /> Verified sellers
              </li>
              <li className="flex flex-col items-center gap-1 rounded-xl bg-white p-2.5 ring-1 ring-ink-200/70">
                <MapPin className="h-5 w-5 text-ink-700" /> Local stores
              </li>
            </ul>

            {/* Other sellers */}
            {others.length > 0 && (
              <section id="other-sellers" className="mt-6 scroll-mt-28">
                <h2 className="text-base font-semibold text-ink-900">Other sellers for {selected.label}</h2>
                <ul className="mt-3 space-y-2">
                  {others.map((o) => (
                    <li key={o.listingId} className="flex flex-wrap items-center gap-3 rounded-2xl bg-white p-4 ring-1 ring-ink-200/70">
                      <div className="min-w-0 flex-1">
                        <PriceTag price={o.price} mrp={o.mrp} discount={o.discountPercent} size="md" />
                        <p className="mt-1 text-sm text-ink-700">
                          <Link to={`/shop/store/${o.seller.slug}`} className="font-semibold text-brand-secondary hover:underline">
                            {o.seller.name}
                          </Link>
                          {o.seller.rating > 0 && (
                            <span className="ml-1.5 inline-flex items-center gap-0.5 text-xs text-ink-500">
                              <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                              {Number(o.seller.rating).toFixed(1)}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-ink-500">
                          Delivery by <span className="font-medium text-ink-700">{o.delivery?.label}</span>
                          {o.available <= 5 && <span className="ml-2 font-semibold text-danger">Only {o.available} left</span>}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => add(o.listingId, Math.min(qty, o.maxPerOrder, o.available))}
                        disabled={busy != null || o.available < 1}
                        className="lk-btn-ghost min-h-[42px] px-4 py-2"
                      >
                        {busy === o.listingId ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
                        Add
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Highlights */}
            {(product.highlights?.length > 0 || keySpecs.length > 0) && (
              <section className="mt-6 rounded-2xl bg-white p-4 ring-1 ring-ink-200/70 sm:p-5">
                <h2 className="text-base font-semibold text-ink-900">Highlights</h2>
                <ul className="mt-3 space-y-2">
                  {(product.highlights || []).map((h) => (
                    <li key={h} className="flex gap-2 text-sm text-ink-700">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                      {h}
                    </li>
                  ))}
                </ul>
                {keySpecs.length > 0 && (
                  <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-ink-200/70 pt-4 sm:grid-cols-4">
                    {keySpecs.map((s) => (
                      <div key={s.key}>
                        <dt className="text-xs text-ink-500">{s.label}</dt>
                        <dd className="text-sm font-semibold text-ink-900">{s.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </section>
            )}
          </div>
        </div>

        {/* Description + specs */}
        <div className="mt-8 grid items-start gap-6 lg:grid-cols-2">
          {product.description && (
            <section className="lk-card p-5 sm:p-6">
              <h2 className="text-h3 font-semibold text-ink-900">Description</h2>
              <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink-700">{product.description}</p>
            </section>
          )}
          <section className="lk-card p-5 sm:p-6">
            <h2 className="text-h3 font-semibold text-ink-900">Specifications</h2>
            <dl className="mt-3 divide-y divide-ink-200/70 text-sm">
              {[
                ...(detail.specs || []),
                ...(selected.sku ? [{ key: 'sku', label: 'SKU', value: selected.sku }] : []),
                ...(selected.netQuantity ? [{ key: 'net', label: 'Net quantity', value: `${selected.netQuantity} ${selected.netUnit || ''}` }] : []),
                ...(product.countryOfOrigin ? [{ key: 'coo', label: 'Country of origin', value: product.countryOfOrigin }] : []),
                ...(product.manufacturerInfo ? [{ key: 'mfr', label: 'Manufacturer', value: product.manufacturerInfo }] : []),
              ].map((s) => (
                <div key={s.key} className="grid grid-cols-[40%_1fr] gap-3 py-2.5">
                  <dt className="text-ink-500">{s.label}</dt>
                  <dd className="text-ink-900">{s.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        </div>

        <div className="mt-6">
          <ProductReviews slug={slug} rating={product.rating} />
        </div>

        {detail.similar?.length > 0 && (
          <section className="mt-8">
            <h2 className="lk-heading mb-4">Similar products</h2>
            <ProductRail items={detail.similar} />
          </section>
        )}
      </div>

      {/* Phones/tablets: sticky price + CTAs */}
      <div className="h-24 lg:hidden" />
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-ink-200 bg-white/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 shadow-bar backdrop-blur lg:hidden">
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <div className="min-w-0 shrink-0">
            <p className="tabular text-lg font-bold leading-tight text-ink-900">{rupees(price)}</p>
            {discount > 0 && <p className="tabular text-[11px] font-semibold text-success">{discount}% off</p>}
          </div>
          {offer ? (
            <div className="flex min-w-0 flex-1 gap-2">{ctaButtons(true)}</div>
          ) : (
            <p className="flex-1 text-right text-sm font-semibold text-danger">Currently unavailable</p>
          )}
        </div>
      </div>

      {detail.sizeChart && (
        <Sheet open={chartOpen} onClose={() => setChartOpen(false)} title="Size chart" wide>
          <SizeChart chart={detail.sizeChart} />
        </Sheet>
      )}
    </ShopChrome>
  );
};

export default ProductPage;
